import React from 'react';
import { Box, Typography, Card, CardContent, CardActionArea } from '@mui/material';
import { Title, useTranslate, usePermissions } from 'react-admin';
import {
  TrendingUp,
  Receipt,
  Assessment,
  Speed,
  BatteryChargingFull,
  ShowChart,
  Business,
  LocationOn,
  PeopleAlt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  path: string;
  permission: string;
  category: 'financial' | 'operational' | 'vendor';
}

export const ReportsDashboard = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { permissions } = usePermissions();

  const reports: ReportCard[] = [
    // Financial Reports
    {
      id: 'revenue-analysis',
      title: translate('reports.revenue.title', { fallback: 'Revenue Analysis' }),
      description: translate('reports.revenue.description', {
        fallback: 'Track revenue trends by time period with site/charger/vendor breakdowns',
      }),
      icon: TrendingUp,
      color: '#009A75',
      path: '/reports/financial/revenue',
      permission: 'reports:revenue:read',
      category: 'financial',
    },
    {
      id: 'transaction-volume',
      title: translate('reports.transactions.title', { fallback: 'Transaction Volume' }),
      description: translate('reports.transactions.description', {
        fallback: 'Analyze transaction patterns and payment methods',
      }),
      icon: Receipt,
      color: '#2196F3',
      path: '/reports/financial/transactions',
      permission: 'reports:transactions:read',
      category: 'financial',
    },

    // Operational Reports
    {
      id: 'session-analytics',
      title: translate('reports.sessions.title', { fallback: 'Session Analytics' }),
      description: translate('reports.sessions.description', {
        fallback: 'Deep dive into charging session patterns and usage',
      }),
      icon: Assessment,
      color: '#FFB800',
      path: '/reports/operational/sessions',
      permission: 'reports:sessions:read',
      category: 'operational',
    },
    {
      id: 'charger-utilization',
      title: translate('reports.utilization.title', { fallback: 'Charger Utilization' }),
      description: translate('reports.utilization.description', {
        fallback: 'Track charger usage efficiency and uptime',
      }),
      icon: Speed,
      color: '#FF6B6B',
      path: '/reports/operational/charger-utilization',
      permission: 'reports:charger-utilization:read',
      category: 'operational',
    },
    {
      id: 'energy-consumption',
      title: translate('reports.energy.title', { fallback: 'Energy Consumption' }),
      description: translate('reports.energy.description', {
        fallback: 'Track energy delivery patterns and cost breakdown',
      }),
      icon: BatteryChargingFull,
      color: '#4CAF50',
      path: '/reports/operational/energy-consumption',
      permission: 'reports:energy-consumption:read',
      category: 'operational',
    },
    {
      id: 'peak-usage',
      title: translate('reports.peak.title', { fallback: 'Peak Usage Analysis' }),
      description: translate('reports.peak.description', {
        fallback: 'Identify peak usage patterns for capacity planning',
      }),
      icon: ShowChart,
      color: '#9C27B0',
      path: '/reports/operational/peak-usage',
      permission: 'reports:peak-usage:read',
      category: 'operational',
    },
    {
      id: 'user-activity',
      title: translate('reports.userActivity.title', { fallback: 'User Activity' }),
      description: translate('reports.userActivity.description', {
        fallback: 'App-user charging activity aggregated per user with contact info for follow-up',
      }),
      icon: PeopleAlt,
      color: '#3F51B5',
      path: '/reports/operational/user-activity',
      permission: 'reports:user-activity:read',
      category: 'operational',
    },

    // Vendor Reports
    {
      id: 'vendor-performance',
      title: translate('reports.vendorPerformance.title', { fallback: 'Vendor Performance' }),
      description: translate('reports.vendorPerformance.description', {
        fallback: 'Compare performance metrics across all vendors',
      }),
      icon: Business,
      color: '#FF9800',
      path: '/reports/vendor/performance',
      permission: 'reports:vendor-performance:read',
      category: 'vendor',
    },
    {
      id: 'site-comparison',
      title: translate('reports.siteComparison.title', { fallback: 'Site Comparison' }),
      description: translate('reports.siteComparison.description', {
        fallback: 'Compare all sites across vendors',
      }),
      icon: LocationOn,
      color: '#00BCD4',
      path: '/reports/vendor/sites',
      permission: 'reports:site-comparison:read',
      category: 'vendor',
    },
  ];

  const hasPermission = (permission: string) => {
    if (!permissions) return false;
    return permissions.includes(permission) || permissions.includes('*:*:*');
  };

  const visibleReports = reports.filter((report) => hasPermission(report.permission));

  const groupedReports = {
    financial: visibleReports.filter((r) => r.category === 'financial'),
    operational: visibleReports.filter((r) => r.category === 'operational'),
    vendor: visibleReports.filter((r) => r.category === 'vendor'),
  };

  const categoryTitles = {
    financial: translate('reports.categories.financial', { fallback: 'Financial Reports' }),
    operational: translate('reports.categories.operational', { fallback: 'Operational Reports' }),
    vendor: translate('reports.categories.vendor', { fallback: 'Vendor Reports' }),
  };

  const categoryDescriptions = {
    financial: translate('reports.categories.financialDesc', {
      fallback: 'Revenue tracking, transaction analysis, and billing reports',
    }),
    operational: translate('reports.categories.operationalDesc', {
      fallback: 'Session analytics, utilization metrics, and energy consumption',
    }),
    vendor: translate('reports.categories.vendorDesc', {
      fallback: 'Cross-vendor performance comparison and site analytics',
    }),
  };

  const renderCategory = (
    category: 'financial' | 'operational' | 'vendor',
    categoryReports: ReportCard[]
  ) => {
    if (categoryReports.length === 0) return null;

    return (
      <Box key={category} sx={{ mb: { xs: 3, sm: 5 }, minWidth: 0 }}>
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 600,
              color: '#1A1A1A',
              mb: 0.5,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {categoryTitles[category]}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#606060', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
          >
            {categoryDescriptions[category]}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: { xs: 2, sm: 3 },
            minWidth: 0,
          }}
        >
          {categoryReports.map((report) => {
            const Icon = report.icon;
            return (
              <Card
                key={report.id}
                sx={{
                  minWidth: 0,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${report.color} 0%, ${report.color}88 100%)`,
                    borderRadius: '20px 20px 0 0',
                  },
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease',
                  },
                }}
              >
                <CardActionArea onClick={() => navigate(report.path)} sx={{ height: '100%', minWidth: 0 }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 40, sm: 56 },
                          height: { xs: 40, sm: 56 },
                          borderRadius: 2,
                          backgroundColor: `${report.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ fontSize: { xs: 24, sm: 28 }, color: report.color }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#1A1A1A',
                          mb: 1,
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {report.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#606060',
                          lineHeight: 1.6,
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {report.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Title title={translate('reports.dashboard.title', { fallback: 'Reports Dashboard' })} />
      <Box sx={{ backgroundColor: 'transparent', minHeight: 'calc(100vh - 120px)', maxWidth: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, sm: 4 }, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: '1.25rem', sm: '2.125rem' },
              fontWeight: 700,
              color: '#1A1A1A',
              mb: 1,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {translate('reports.dashboard.title', { fallback: 'Reports Dashboard' })}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#606060', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
          >
            {translate('reports.dashboard.subtitle', {
              fallback: 'Access comprehensive reports and analytics for your EV charging network',
            })}
          </Typography>
        </Box>

        {/* Report Categories */}
        {renderCategory('financial', groupedReports.financial)}
        {renderCategory('operational', groupedReports.operational)}
        {renderCategory('vendor', groupedReports.vendor)}

        {/* No Reports Available */}
        {visibleReports.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: '#909090', mb: 1 }}>
              {translate('reports.dashboard.noReports', { fallback: 'No reports available' })}
            </Typography>
            <Typography variant="body2" sx={{ color: '#B0B0B0' }}>
              {translate('reports.dashboard.noReportsDesc', {
                fallback: 'You do not have permission to access any reports',
              })}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};
